import React, { useState, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { UserProfile } from '@llm-macro-tracker/shared';
import { useNavigate, Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SelectWithTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import { logEvent } from 'firebase/analytics';
import { analytics } from '@/services/firebaseService';
import { ProfilePageSkeleton } from '@/components/ProfilePageSkeleton';

// Define constants for calorie limits
const CALORIE_MIN = 500;
const CALORIE_MAX = 10000;

const formSchema = z.object({
  dailyCalorieGoal: z.number().min(CALORIE_MIN).max(CALORIE_MAX),
  macroGoals: z
    .object({
      protein: z.number().min(0).max(100),
      carbs: z.number().min(0).max(100),
      fat: z.number().min(0).max(100),
    })
    .refine(
      (data) => {
        const sum = data.protein + data.carbs + data.fat;
        return sum === 100;
      },
      {
        message: 'Macros must sum up to 100%',
        path: ['macroGoals'],
      },
    ),
  weight: z.union([z.number().positive(), z.literal('')]),
  height: z.union([z.number().positive(), z.literal('')]),
  age: z.number().min(1).max(120),
  gender: z.enum(['male', 'female']),
  activityLevel: z.enum(['sedentary', 'light', 'moderate', 'active', 'very active']),
  weightGoal: z.enum(['lose', 'maintain', 'gain']).optional(),
});

interface MacroSliderProps {
  macro: string;
  color: string;
  value: number;
  onChange: (value: number) => void;
  dailyCalorieGoal: number;
  gramsDisplay: number;
}

const MacroSlider: React.FC<MacroSliderProps> = ({
  macro,
  color,
  value,
  onChange,
  dailyCalorieGoal,
  gramsDisplay,
}) => {
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium">{macro}</span>
        <span className="text-sm font-bold">
          {value}% ({isNaN(gramsDisplay) ? '0' : gramsDisplay}g)
        </span>
      </div>
      <Slider
        min={0}
        max={100}
        step={1}
        value={[value]}
        onValueChange={(newValue) => onChange(Math.round(newValue[0]))}
        className="w-full"
      />
    </div>
  );
};

const calculateRecommendedCalories = (profile: z.infer<typeof formSchema>): number | null => {
  if (
    !profile.weight ||
    !profile.height ||
    !profile.activityLevel ||
    !profile.age ||
    !profile.gender
  ) {
    return null; // Return null if required fields are missing
  }

  // Calculate BMR (Basal Metabolic Rate)
  const weight = Number(profile.weight);
  const height = Number(profile.height);
  const age = Number(profile.age);

  // Harris-Benedict Equation
  let bmr;
  if (profile.gender === 'male') {
    bmr = 88.362 + 13.397 * weight + 4.799 * height - 5.677 * age;
  } else {
    bmr = 447.593 + 9.247 * weight + 3.098 * height - 4.33 * age;
  }

  // Activity factor
  const activityFactors = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    'very active': 1.9,
  };

  const activityFactor = activityFactors[profile.activityLevel];

  // Calculate TDEE (Total Daily Energy Expenditure)
  let tdee = bmr * activityFactor;

  // Adjust based on weight goal
  if (profile.weightGoal === 'lose') {
    tdee -= 500; // Subtract 500 calories for weight loss
  } else if (profile.weightGoal === 'gain') {
    tdee += 500; // Add 500 calories for weight gain
  }

  return Math.round(tdee);
};

interface MacroGoals {
  protein: number;
  carbs: number;
  fat: number;
}

export const ProfilePage: React.FC = () => {
  const { userProfile, updateUserProfile } = useUser();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [macros, setMacros] = useState<MacroGoals>(
    userProfile?.macroGoals || { protein: 30, carbs: 40, fat: 30 },
  );
  const [estimatedCalories, setEstimatedCalories] = useState<number | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [calorieInputValue, setCalorieInputValue] = useState<string>('');

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      dailyCalorieGoal: userProfile?.dailyCalorieGoal || 2000,
      macroGoals: userProfile?.macroGoals || { protein: 30, carbs: 40, fat: 30 },
      weight: userProfile?.weight || '',
      height: userProfile?.height || '',
      age: userProfile?.age || 30,
      gender: userProfile?.gender || 'male',
      activityLevel: userProfile?.activityLevel || 'moderate',
      weightGoal: userProfile?.weightGoal || 'maintain',
    },
  });

  useEffect(() => {
    if (userProfile) {
      form.reset({
        ...userProfile,
        dailyCalorieGoal: userProfile.dailyCalorieGoal || 2000,
        weight: userProfile.weight ?? '',
        height: userProfile.height ?? '',
      });
      setMacros(userProfile.macroGoals || { protein: 30, carbs: 40, fat: 30 });
      setIsLoading(false);
    }
  }, [userProfile, form]);

  useEffect(() => {
    if (userProfile?.dailyCalorieGoal) {
      setCalorieInputValue(userProfile.dailyCalorieGoal.toString());
    }
  }, [userProfile]);

  const handleMacroChange = (macro: keyof MacroGoals) => (value: number) => {
    setMacros((prev) => ({ ...prev, [macro]: value }));
  };

  const totalPercentage = Object.values(macros).reduce((sum, val) => sum + val, 0);
  const isValidTotal = totalPercentage === 100;

  const handleCalculateCalories = () => {
    const values = form.getValues();
    const requiredFields = ['weight', 'height', 'age', 'gender', 'activityLevel'] as const;
    const missingFields = requiredFields.filter((field) => !values[field]);

    if (missingFields.length > 0) {
      setEstimateError(`Please fill in the following fields: ${missingFields.join(', ')}`);
      setEstimatedCalories(null);
    } else {
      const calories = calculateRecommendedCalories(values);
      setEstimatedCalories(calories);
      setEstimateError(null);
    }
  };

  const handleApplyEstimatedCalories = () => {
    if (estimatedCalories !== null) {
      form.setValue('dailyCalorieGoal', estimatedCalories);
      setCalorieInputValue(estimatedCalories.toString());
    }
    setEstimatedCalories(null);
  };

  const handleCustomCalorieChange = (value: string) => {
    setCalorieInputValue(value);
    const numberValue = value === '' ? null : Number(value);
    form.setValue('dailyCalorieGoal', numberValue ?? 0);
  };

  const calculateMacroGrams = (macroType: 'protein' | 'carbs' | 'fat', percentage: number) => {
    const calories = form.getValues().dailyCalorieGoal;
    const caloriesFromMacro = (calories * percentage) / 100;
    const caloriesPerGram = macroType === 'fat' ? 9 : 4;
    return Math.round(caloriesFromMacro / caloriesPerGram);
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const updatedProfile: UserProfile = {
        v: 1, // Add this line to specify the version
        dailyCalorieGoal: values.dailyCalorieGoal || userProfile?.dailyCalorieGoal || CALORIE_MIN,
        macroGoals: macros,
        activityLevel: values.activityLevel,
        weightGoal: values.weightGoal,
        weight: values.weight === '' ? null : Number(values.weight),
        height: values.height === '' ? null : Number(values.height),
        age: values.age,
        gender: values.gender,
      };

      await updateUserProfile(updatedProfile);
      navigate('/');
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  useEffect(() => {
    logEvent(analytics, 'page_view', { page_title: 'Profile Page' });
  }, []);

  if (isLoading) {
    return <ProfilePageSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-400 to-purple-500 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Your Profile 📊</CardTitle>
          <CardDescription>Set your nutrition goals and personal information</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Weight input */}
              <FormField
                control={form.control}
                name="weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Weight (kg)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value === null ? '' : field.value}
                        onChange={(e) =>
                          field.onChange(e.target.value ? Number(e.target.value) : '')
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Height input */}
              <FormField
                control={form.control}
                name="height"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Height (cm) 📏</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value === null ? '' : field.value}
                        onChange={(e) =>
                          field.onChange(e.target.value ? Number(e.target.value) : '')
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Age input */}
              <FormField
                control={form.control}
                name="age"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Age 🎂</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value ? Number(e.target.value) : '')
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Gender select */}
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender</FormLabel>
                    <FormControl>
                      <SelectWithTrigger onValueChange={field.onChange} defaultValue={field.value}>
                        <SelectValue placeholder="Select gender" />
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </SelectWithTrigger>
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Activity Level select */}
              <FormField
                control={form.control}
                name="activityLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Activity Level 🏃‍♀️</FormLabel>
                    <FormControl>
                      <SelectWithTrigger onValueChange={field.onChange} defaultValue={field.value}>
                        <SelectValue placeholder="Select activity level" />
                        <SelectContent>
                          <SelectItem value="sedentary">Sedentary</SelectItem>
                          <SelectItem value="light">Light</SelectItem>
                          <SelectItem value="moderate">Moderate</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="very active">Very Active</SelectItem>
                        </SelectContent>
                      </SelectWithTrigger>
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Weight Goal select */}
              <FormField
                control={form.control}
                name="weightGoal"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Weight Goal 🎯</FormLabel>
                    <FormControl>
                      <SelectWithTrigger onValueChange={field.onChange} defaultValue={field.value}>
                        <SelectValue placeholder="Select weight goal" />
                        <SelectContent>
                          <SelectItem value="lose">Lose Weight</SelectItem>
                          <SelectItem value="maintain">Maintain Weight</SelectItem>
                          <SelectItem value="gain">Gain Weight</SelectItem>
                        </SelectContent>
                      </SelectWithTrigger>
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Daily Calorie Goal and Estimate Button */}
              <FormField
                control={form.control}
                name="dailyCalorieGoal"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Daily Calorie Goal 🍽️</FormLabel>
                    <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2">
                      <FormControl className="w-full sm:w-1/3">
                        <Input
                          type="number"
                          min={CALORIE_MIN}
                          max={CALORIE_MAX}
                          value={calorieInputValue}
                          onChange={(e) => handleCustomCalorieChange(e.target.value)}
                        />
                      </FormControl>
                      <Button
                        type="button"
                        onClick={handleCalculateCalories}
                        className="w-full sm:w-auto"
                      >
                        Estimate Calories
                      </Button>
                      <Link
                        to="/calorie-estimation-explanation"
                        className="text-blue-500 hover:underline text-sm whitespace-nowrap"
                      >
                        <InfoCircledIcon className="h-4 w-4 inline mr-1" />
                        How is this calculated?
                      </Link>
                    </div>
                    <FormDescription className="mt-1">
                      Set your daily calorie target or use the estimate button
                    </FormDescription>
                  </FormItem>
                )}
              />
              {estimatedCalories !== null && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">
                    Estimated: {estimatedCalories} calories
                  </span>
                  <Button type="button" onClick={handleApplyEstimatedCalories} size="sm">
                    Apply
                  </Button>
                </div>
              )}
              {estimateError && <p className="text-sm text-red-600">{estimateError}</p>}

              <div>
                <h3 className="text-lg font-semibold mb-4">Macro Goals (%)</h3>
                <FormField
                  control={form.control}
                  name="macroGoals.protein"
                  render={({ field }) => (
                    <MacroSlider
                      macro="Protein"
                      color="#FF6B6B"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        handleMacroChange('protein')(value);
                      }}
                      dailyCalorieGoal={form.getValues().dailyCalorieGoal}
                      gramsDisplay={calculateMacroGrams('protein', field.value)}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name="macroGoals.carbs"
                  render={({ field }) => (
                    <MacroSlider
                      macro="Carbs"
                      color="#4ECDC4"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        handleMacroChange('carbs')(value);
                      }}
                      dailyCalorieGoal={form.getValues().dailyCalorieGoal}
                      gramsDisplay={calculateMacroGrams('carbs', field.value)}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name="macroGoals.fat"
                  render={({ field }) => (
                    <MacroSlider
                      macro="Fat"
                      color="#FFA400"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        handleMacroChange('fat')(value);
                      }}
                      dailyCalorieGoal={form.getValues().dailyCalorieGoal}
                      gramsDisplay={calculateMacroGrams('fat', field.value)}
                    />
                  )}
                />
                <div
                  className={`text-center mt-6 font-bold ${
                    isValidTotal ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  Total: {totalPercentage}%
                  {!isValidTotal && (
                    <p className="text-sm font-normal mt-2">
                      Please adjust the values to sum up to 100%.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => navigate('/')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!isValidTotal}>
                  Save Profile
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
